/**
 * Is the on-screen keyboard covering the viewport?
 *
 * Needed because iOS does not treat `position: fixed` the way a docked bar
 * wants. When the keyboard opens, Safari lifts fixed elements to sit *above* it
 * rather than leaving them at the bottom of the page, so a player bar and tab
 * bar pinned to `bottom-0` end up floating across the middle of the screen with
 * the keyboard beneath them. Nothing in CSS expresses "and hide when the
 * keyboard is up", so the layout has to be told.
 *
 * The signal is the gap between the layout viewport and the visual one. A
 * threshold rather than any-difference: collapsing browser chrome moves the
 * visual viewport by a few dozen pixels and must not read as a keyboard.
 */

import { useEffect, useState } from 'react'

/** Smaller than any phone keyboard, comfortably larger than a URL bar. */
const KEYBOARD_MIN_HEIGHT = 150

export function useKeyboardOpen(): boolean {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return

    function measure() {
      const covered = window.innerHeight - (viewport?.height ?? 0)
      setOpen(covered > KEYBOARD_MIN_HEIGHT)
    }

    measure()
    viewport.addEventListener('resize', measure)
    // Scrolling the visual viewport happens as the keyboard animates in.
    viewport.addEventListener('scroll', measure)
    return () => {
      viewport.removeEventListener('resize', measure)
      viewport.removeEventListener('scroll', measure)
    }
  }, [])

  return open
}
